import React from 'react'
import {
  Box,
  Card,
  Flex,
  Label,
  LabelVariant,
  Message,
  Text,
} from '@workwhile/ui'
import { CopyableCode } from 'components/Shared/CopyableCode'
import {
  HighnoteAchProvider,
  HighnoteProvider,
  HighnoteUftProvider,
} from 'api/payments/getUserPayments'
import { toTitleCase } from 'lib/string'
import { useHighnoteErrorCodes } from 'queries/admin/highnote/useHighnoteErrorCodes'
import { HighnoteAuditTimeline } from './HighnoteAuditTimeline'
import { PayoutDestinationDetails } from './PayoutDestination'

const getTransferStatusVariant = (status: string): LabelVariant => {
  const normalized = status.toLowerCase()
  if (['processed', 'completed'].includes(normalized)) {
    return 'success'
  }
  if (['failed', 'returned', 'canceled'].includes(normalized)) {
    return 'error'
  }
  if (
    ['pending', 'processing', 'initiated', 'record_created'].includes(
      normalized
    )
  ) {
    return 'information'
  }
  return 'neutral'
}

const TransferIdentity = ({
  highnote,
  children,
}: {
  highnote: HighnoteProvider
  children?: React.ReactNode
}) => (
  <Flex flexDirection="column" gap={1}>
    <Text fontSize="sm" lineHeight="1.4">
      ID: <CopyableCode value={String(highnote.transferId)} />
      {highnote.transferStatus ? (
        <>
          {' · '}
          <Label
            as="span"
            variant={getTransferStatusVariant(highnote.transferStatus)}
            size="small"
          >
            {toTitleCase(highnote.transferStatus)}
          </Label>
        </>
      ) : null}
    </Text>
    {highnote.highnoteId ? (
      <Text fontSize="sm" lineHeight="1.4">
        Highnote ref: <CopyableCode value={highnote.highnoteId} />
      </Text>
    ) : null}
    {children}
  </Flex>
)

const TransferFailure = ({ highnote }: { highnote: HighnoteProvider }) => {
  if (!highnote.failureReason) {
    return null
  }

  return (
    <Message variant="error" mt={2}>
      Failure:{' '}
      {highnote.failureReasonDescription ? (
        <>
          <Text as="span" fontWeight="bold">
            {toTitleCase(highnote.failureReason)}
          </Text>
          <Text as="div" mt={1} fontWeight="normal">
            {highnote.failureReasonDescription}
          </Text>
        </>
      ) : (
        toTitleCase(highnote.failureReason)
      )}
    </Message>
  )
}

const UftPaymentDetails = ({ highnote }: { highnote: HighnoteUftProvider }) => (
  <Card shadowless p={3} flex={1}>
    <Text fontSize={2} fontWeight="bold" mb={1}>
      Unified funds transfer
    </Text>
    <TransferIdentity highnote={highnote} />
    <TransferFailure highnote={highnote} />
  </Card>
)

const AchPaymentDetails = ({ highnote }: { highnote: HighnoteAchProvider }) => (
  <Card shadowless p={3} flex={1}>
    <Text fontSize={2} fontWeight="bold" mb={1}>
      ACH transfer
    </Text>
    <TransferIdentity highnote={highnote}>
      {highnote.traceId ? (
        <Text fontSize="sm" lineHeight="1.4">
          ACH trace ID: <CopyableCode value={highnote.traceId} />
        </Text>
      ) : null}
    </TransferIdentity>
    <TransferFailure highnote={highnote} />
  </Card>
)

type HighnotePaymentDetailsProps = {
  userTimezone: string
  highnote: HighnoteProvider
}

export const HighnotePaymentDetails = ({
  userTimezone,
  highnote,
}: HighnotePaymentDetailsProps) => {
  const { data: highnoteErrorCodes } = useHighnoteErrorCodes()
  const auditEvents = [...(highnote.auditEvents ?? [])].sort(
    (a, b) =>
      new Date(a.originatedAt).getTime() - new Date(b.originatedAt).getTime()
  )

  return (
    <Flex flexDirection="column" gap={3}>
      <Flex
        flexDirection={['column', 'column', 'row']}
        gap={3}
        alignItems="stretch"
      >
        {highnote.transferType === 'originated_ach_transfer' ? (
          <AchPaymentDetails highnote={highnote} />
        ) : (
          <UftPaymentDetails highnote={highnote} />
        )}

        {highnote.paymentMethod ? (
          <Card shadowless p={3} flex={1}>
            <Text fontSize={2} fontWeight="bold" mb={1}>
              Destination
            </Text>
            <PayoutDestinationDetails paymentMethod={highnote.paymentMethod} />
          </Card>
        ) : null}
      </Flex>

      <Card shadowless p={3}>
        <Flex alignItems="baseline" gap={1} mb={2}>
          <Text fontSize={2} fontWeight="bold">
            Audit timeline
          </Text>
          {auditEvents.length ? (
            <Text fontSize={1} color="lightText">
              ({auditEvents.length} event{auditEvents.length === 1 ? '' : 's'})
            </Text>
          ) : null}
        </Flex>

        <HighnoteAuditTimeline
          auditEvents={auditEvents}
          targetTimezone={userTimezone}
        />

        {highnote.errorCode ? (
          <Box mt={2} pt={2} borderTop="1px solid" borderColor="neutrals.100">
            <Text fontSize="sm" fontWeight="bold" color="error">
              Code {highnote.errorCode}
            </Text>
            {highnoteErrorCodes?.byCode[highnote.errorCode]?.description ? (
              <Text fontSize="sm" color="error" mt={1}>
                {highnoteErrorCodes.byCode[highnote.errorCode].description}
              </Text>
            ) : null}
          </Box>
        ) : null}
      </Card>
    </Flex>
  )
}
